import { BlockStack, Card, Layout, SkeletonBodyText, SkeletonDisplayText, SkeletonPage } from '@shopify/polaris';
import React from 'react';
import { useRenderHint } from 'hooks/useRenderHint';

// Suspense fallback for the DASHBOARD route specifically (Router.js gives the
// home route its own boundary; other routes keep the generic RouteSkeleton).
//
// Why it exists: re-entering the app from a Shopify admin menu reboots the
// iframe from scratch, so the merchant watches skeleton→real every single
// time. A generic skeleton can't know that THIS shop's dashboard opens with a
// review banner and a Get-started card on top — so for ~1s everything sat too
// high, then the real sections landed and pushed it all down ("elements jump,
// then settle"). This skeleton reads the same useRenderHint entries the real
// sections maintain, and reserves exactly the boxes the real first paint will
// occupy — including their measured heights — so the swap is positionally
// invisible.
//
// Self-contained inline styles on purpose: the pmqBanner/pmqCard classes live
// in the dashboard chunk's CSS, which by definition hasn't loaded while this
// fallback is showing.
const pendingBlock = (minHeight, radius = 12) => ({
  minHeight,
  background: '#f1f5f9',
  border: '1px solid #e1e6eb',
  borderRadius: radius,
});

const DashboardSkeleton = () => {
  // Same keys the real components report into — see Dashboard.js and
  // PrimeMoqGetStarted.js.
  const { hint: reviewHint } = useRenderHint('primeMoqDash:reviewBanner');
  const { hint: getStartedHint } = useRenderHint('primeMoqDash:getStarted');

  return (
    <SkeletonPage primaryAction>
      <Layout>
        {/* Review banner slot — only when the last settled visit showed it,
            at its measured height, exactly like the real page's pending
            placeholder. */}
        {reviewHint.visible && (
          <Layout.Section>
            <div style={pendingBlock(reviewHint.height || 84, 10)} aria-hidden="true" />
          </Layout.Section>
        )}

        {/* Get-started checklist slot — ditto. */}
        {getStartedHint.visible && (
          <Layout.Section>
            <div style={pendingBlock(getStartedHint.height || 300, 14)} aria-hidden="true" />
          </Layout.Section>
        )}

        {/* Status strip (PrimeMoqStatusBar ≈ 56px) */}
        <Layout.Section>
          <div style={{ minHeight: 56 }} aria-hidden="true">
            <Card>
              <SkeletonBodyText lines={1} />
            </Card>
          </div>
        </Layout.Section>

        {/* Quick-start templates card (≈ 6 template rows) */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={7} />
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Date-range control row */}
        <Layout.Section>
          <div style={{ minHeight: 36, maxWidth: 220 }} aria-hidden="true">
            <SkeletonDisplayText size="small" />
          </div>
        </Layout.Section>

        {/* Stats card: heading + subtitle + tile row */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={5} />
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Blocked-value section: tiles + 220px chart + per-rule table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={4} />
              <div style={{ height: 220 }} aria-hidden="true">
                <SkeletonBodyText lines={2} />
              </div>
              <SkeletonBodyText lines={5} />
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Cross-sell banner + footer links */}
        <Layout.Section>
          <div style={pendingBlock(84, 10)} aria-hidden="true" />
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  );
};

export default DashboardSkeleton;
